import useSWR, { mutate } from 'swr';
import { fetcher, fetcherDelete, fetcherPost, fetcherPut, fetcherWithMethod } from 'utils/axios';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { CrudEndpoints } from 'types/common';
import { useMemo } from 'react';
import axios from 'axios';

export const endpoints: CrudEndpoints = {
    key: 'TaskNote',
    getList: '/GetList',
    getListById: '/GetList?TaskID={ID}',
    getModal: '/',
    getLookupList: '/GetLookupList',
    insert: '/',
    update: '/',
    delete: ''
};

export function useGetTaskNoteList(
    taskId: number | string,
    taskNoteId?: number | string | null,
    shouldFetch: boolean = true
) {
    const params = new URLSearchParams();
    if (taskId !== undefined && taskId !== null && taskId !== '') {
        params.append('TaskID', taskId.toString());
    }
    if (taskNoteId !== undefined && taskNoteId !== null && taskNoteId !== '') {
        params.append('TaskNoteID', taskNoteId.toString());
    }

    const queryString = params.toString();
    const endpoint = taskId ? `${endpoints.key}${endpoints.getList}?${queryString}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        shouldFetch && endpoint ? [endpoint, { method: 'GET' }] : null,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            keepPreviousData: true,
            onError: (err) => {
                handleApiError(err);
            }
        }
    );

    return useMemo(
        () => ({
            data: data || [],
            rawNotesData: data,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && (!data || (Array.isArray(data) && data.length === 0))
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export function useGetTaskNoteModal(taskNoteId?: number | string | null, shouldFetch: boolean = true) {
    const endpoint = taskNoteId ? `${endpoints.key}/${taskNoteId}` : null;

    const { data, isLoading, error, isValidating, mutate } = useSWR(
        shouldFetch && endpoint ? [endpoint, { method: 'GET' }] : null,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            onError: (err) => {
                handleApiError(err);
            }
        }
    );

    return useMemo(
        () => ({
            data: data || null,
            isLoading,
            error,
            mutate,
            isValidating,
            isEmpty: !isLoading && !data
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const useInsertTaskNote = async (data: any) => {
    try {
        const response = await fetcherPost([`${endpoints.key}${endpoints.insert}`, { data }]);
        showSuccess('Task note created successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useUpdateTaskNote = async (data: any) => {
    try {
        const response = await fetcherPut([`${endpoints.key}${endpoints.update}`, { data }]);
        showSuccess('Task note updated successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

export const useDeleteTaskNote = async (taskNoteId: number | string) => {
    try {
        const response = await fetcherDelete([`${endpoints.key}?TaskNoteID=${taskNoteId}`, {}]);
        showSuccess('Task note deleted successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// ==================== Task Note File Management APIs ====================

export interface GenerateNoteUploadFileUrlsPayload {
    taskNoteID: number;
    fileNames: string[];
}

export interface GenerateNoteBlobFileIDPayload {
    taskNoteID: number;
    fileName: string;
}

export interface GetNoteFileUrlParams {
    taskNoteID: number | string;
    fileID?: string;
    fileName?: string;
}

export interface DeleteTaskNoteFilePayload {
    taskNoteID: number | string;
    fileID?: string;
    fileName?: string;
}

// 1. GenerateUploadFileUrls for Note (POST)
export const useGenerateUploadFileUrlsForNote = async (payload: GenerateNoteUploadFileUrlsPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateUploadFileUrls`, { data: payload, timeout: 0 }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 2. GenerateBlobFileID for Note (POST)
export const useAfterSuccessfulFileUploadForNote = async (payload: GenerateNoteBlobFileIDPayload) => {
    try {
        const response = await fetcherPost([`${endpoints.key}/GenerateBlobFileID`, { data: payload, timeout: 0 }]);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 3. GetFileUrl for Note (GET)
export const useGetNoteFileUrl = async ({ taskNoteID, fileID }: GetNoteFileUrlParams) => {
    try {
        const params: string[] = [];
        if (taskNoteID !== undefined && taskNoteID !== null) params.push(`taskNoteID=${encodeURIComponent(taskNoteID)}`);
        if (fileID && fileID !== 'undefined' && fileID !== 'null' && fileID !== '0') {
            params.push(`fileID=${encodeURIComponent(fileID)}`);
        }
        const queryString = params.length > 0 ? `?${params.join('&')}` : '';

        const response = await fetcher(`${endpoints.key}/GetFileUrl${queryString}`);
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};

// 4. GetFiles for Note (GET)
export function useGetTaskNoteFiles(taskNoteID?: number | string | null) {
    const endpoint = taskNoteID ? `${endpoints.key}/GetFiles/${taskNoteID}` : null;

    const { data, isLoading, isValidating, error, mutate } = useSWR(
        endpoint,
        fetcher,
        {
            revalidateIfStale: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    return useMemo(
        () => ({
            data: data || [],
            isLoading,
            isValidating,
            error,
            mutate
        }),
        [data, error, isLoading, isValidating, mutate]
    );
}

export const mutateTaskNoteFiles = (taskNoteID: number | string) => {
    if (!taskNoteID) return;
    const strId = String(taskNoteID);
    mutate((key: any) => {
        if (!key) return false;
        const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
        return (
            keyStr.includes(`TaskNote/GetFiles/${strId}`) ||
            keyStr.includes(`ProjectFileManager/GetList`)
        );
    });
};

// 5. DeleteFile for Note (DELETE)
export const useDeleteTaskNoteFile = async (payload: DeleteTaskNoteFilePayload) => {
    try {
        const response = await fetcherDelete([`${endpoints.key}/DeleteFile`, { data: payload }]);
        showSuccess('File deleted successfully');
        return response;
    } catch (error) {
        handleApiError(error);
        throw error;
    }
};